import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  GetCurrentResponseDto,
  PromoDto,
  TeamDto,
  TeamSearchDecisionMakerCategoryDto,
  TeamSettingDto,
  UserDto,
  UserSessionDto,
  UserSettingDto,
} from "api/Api";
import dayjs from "dayjs";
import { RootState } from "store";

export type AuthNewStateType = {
  frontendVersion?: string | null;
  intercomHash?: string | null;
  loaded?: boolean;
  promo?: PromoDto | null;
  searchDecisionMakerCategoryList: TeamSearchDecisionMakerCategoryDto[];
  team?: TeamDto;
  teamOwner?: UserDto;
  teamSetting?: TeamSettingDto;
  teamTrialAvailable?: boolean;
  user?: UserDto;
  userRoleList?: string[];
  userSession?: UserSessionDto;
  userSetting?: UserSettingDto;
};

const initialState: AuthNewStateType = {
  searchDecisionMakerCategoryList: [],
};

export const authNewSlice = createSlice({
  initialState,
  name: "authNew",
  reducers: {
    _set: (state, { payload }: PayloadAction<GetCurrentResponseDto>) => {
      state.frontendVersion = payload.frontendVersion;
      state.intercomHash = payload.intercomHash;
      state.loaded = true;
      state.promo = payload.promo;
      state.searchDecisionMakerCategoryList =
        payload.searchDecisionMakerCategoryList;
      state.team = payload.team || undefined;
      state.teamOwner = payload.teamOwner || undefined;
      state.teamSetting = payload.teamSetting || undefined;
      state.teamTrialAvailable = payload.teamTrialAvailable;
      state.user = payload.user || undefined;
      state.userRoleList = payload.userRoleList || [];
      state.userSession = payload.userSession || undefined;
      state.userSetting = payload.userSetting || undefined;
    },
    creditLeftActiveSet: (state, { payload }: PayloadAction<number>) => {
      if (state.team) {
        state.team.creditLeftActive = payload;
      }
    },
    frontendVersionSet: (state, { payload }: PayloadAction<string>) => {
      state.frontendVersion = payload;
    },
    teamSubscriptionTrialFraudDetectedSet: (
      state,
      { payload }: PayloadAction<boolean>,
    ) => {
      if (state.team) {
        state.team.subscriptionTrialStatus = payload ? "fraud_detected" : null;
      }
    },
  },
});

const promoGet = (state: RootState) => {
  const promo = state.authNew?.promo;
  if (!promo || dayjs.utc(promo.endAt).isBefore(dayjs().utc())) {
    return null;
  }

  return promo;
};

export const authNewSelectors = {
  _loadedGet: (state: RootState) => state.authNew.loaded === true,
  creditLeftGet: (state: RootState) =>
    state.authNew.team?.creditLeftActive || 0,
  feedbackRiskyShowGet: (state: RootState) =>
    state.authNew.team?.feedbackRiskyShow === true,
  creditLowShowGet: (state: RootState) => {
    const subscription = authNewSelectors.subscriptionGet(state);

    return (
      subscription &&
      subscription.trialEndAt == null &&
      authNewSelectors.creditLeftGet(state) <=
        Math.ceil((subscription.plan.creditPerPeriod / 100) * 10) &&
      authNewSelectors.teamSettingCreditAutoRefillThresholdPercentageGet(
        state,
      ) === null
    );
  },
  frontendVersionGet: (state: RootState) => state.authNew.frontendVersion,
  hasPurchase: (state: RootState) => state.authNew.team?.hasPurchase === true,
  loggedInGet: (state: RootState) =>
    Boolean(state.authNew.team) && Boolean(state.authNew.user),
  promoGet,
  searchCountApiGet: (state: RootState) =>
    state.authNew.team?.searchCountApi || 0,
  searchCountBulkGet: (state: RootState) =>
    state.authNew.team?.searchCountBulk || 0,
  searchDecisionMakerCategoryListGet: (state: RootState) =>
    state.authNew.searchDecisionMakerCategoryList,
  subscriptionGet: (state: RootState) =>
    state.authNew.team?.subscription || null,
  teamBulkAutoPayGet: (state: RootState) => state.authNew.team?.bulkAutoPay,
  teamEmailVerificationStatusGet: (state: RootState) =>
    state.authNew.teamOwner?.emailVerificationStatus,
  teamOwnerEmailGet: (state: RootState) => state.authNew.teamOwner?.email,
  teamOwnerIdGet: (state: RootState) => state.authNew.team?.ownerId,
  teamSamlMetadataUrl: (state: RootState) =>
    state.authNew.team?.samlMetadataUrl,
  teamSettingCreditAutoRefillThresholdPercentageGet: (state: RootState) =>
    state.authNew.teamSetting?.creditAutoRefillThresholdPercentage || null,
  teamSettingLowCreditNotificationThresholdGet: (state: RootState) =>
    state.authNew.teamSetting?.lowCreditNotificationThreshold || 10,
  teamSubscriptionTrialStatusGet: (state: RootState) =>
    state.authNew.team?.subscriptionTrialStatus,
  trialActiveGet: (state: RootState) =>
    state.authNew.team?.subscription?.trialEndAt != null,
  trialAvailableGet: (state: RootState) =>
    !state.authNew.team || state.authNew.teamTrialAvailable === true,
  userAuthPasswordIsSet: (state: RootState) =>
    state.authNew.user?.authPasswordIsSet === true,
  userEmailGet: (state: RootState) => state.authNew.user?.email,
  userIdGet: (state: RootState) => state.authNew.user?.id,
  userIntercomHash: (state: RootState) => state.authNew.intercomHash || null,
  userIsTeamOwner: (state: RootState) =>
    state.authNew.user?.id === state.authNew.team?.ownerId,
  userNameGet: (state: RootState) => state.authNew.user?.name,
  userRoleListGet: (state: RootState) => state.authNew.userRoleList,
  userSamlGet: (state: RootState) => state.authNew.user?.saml === true,
  userSessionGet: (state: RootState) => state.authNew.userSession,
  userSettingFileDoneNotificationGet: (state: RootState) =>
    state.authNew.userSetting?.fileDoneNotification || "on_if_last_in_queue",
};

export const authNewActions = authNewSlice.actions;
export default authNewSlice.reducer;
